import { supabase } from '../supabase'

/**
 * One website stamp trade (3 stamps = 1 month, 20 = 1 year of app
 * time). Granted time sits AFTER any paid period; `used` flips once
 * the whole period has elapsed. Trading happens only on the website.
 */
export interface StampGrant {
  period: 'month' | 'year'
  stamps: number
  at: string
  until: string
  used?: boolean
  usedAt?: string
}

export interface SubscriptionStatus {
  isActive: boolean
  tier: string | null
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | null
  source: 'apple' | 'squarespace' | 'stripe' | 'manual' | 'stamps' | null
  currentPeriodEnd: string | null
  /** Stamp trades on this subscription, oldest first. Null when none. */
  stampGrants: StampGrant[] | null
  /** Where PAID coverage ends (blended end minus unused grant time).
   *  Null for stamps-only rows. When this is in the past but
   *  currentPeriodEnd isn't, stamp time is actively covering. */
  storePeriodEnd: string | null
}

const NONE: SubscriptionStatus = {
  isActive: false, tier: null, status: null, source: null, currentPeriodEnd: null,
  stampGrants: null, storePeriodEnd: null,
}

/**
 * Look up the signed-in user's subscription by the email on their JWT.
 * Returns `NONE` if unauthenticated, unmatched, or the RPC errors out —
 * subscription state is never a hard failure path for the UI.
 */
export async function fetchMySubscription(): Promise<SubscriptionStatus> {
  const { data, error } = await supabase.rpc('current_user_subscription')
  if (error || !data || !Array.isArray(data) || data.length === 0) return NONE

  const row = data[0] as {
    is_active: boolean | null
    tier: string | null
    status: SubscriptionStatus['status']
    source: SubscriptionStatus['source']
    current_period_end: string | null
    stamp_grants: StampGrant[] | null
    store_period_end: string | null
  }

  return {
    isActive: row.is_active ?? false,
    tier: row.tier,
    status: row.status,
    source: row.source,
    currentPeriodEnd: row.current_period_end,
    stampGrants: Array.isArray(row.stamp_grants) && row.stamp_grants.length > 0
      ? row.stamp_grants
      : null,
    storePeriodEnd: row.store_period_end,
  }
}
